import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { BASE_URL, API_ENDPOINTS } from './endpoints';

/**
 * Shared axios instance for the admin console.
 *  - injects the admin Bearer token from localStorage,
 *  - on a 401 (expired/invalid token) tries a one-shot refresh via the admin
 *    refresh endpoint, replays the original request, and only bounces to
 *    /login if the refresh itself fails.
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function clearAndRedirect(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

let isRefreshing = false;

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error?.response?.status;
    const refreshToken = localStorage.getItem('refreshToken');

    const isAuthCall =
      original?.url === API_ENDPOINTS.LOGIN || original?.url === API_ENDPOINTS.REFRESH_TOKEN;

    if (status === 401 && original && !original._retry && refreshToken && !isAuthCall && !isRefreshing) {
      original._retry = true;
      isRefreshing = true;
      try {
        const resp = await axios.post(`${BASE_URL}${API_ENDPOINTS.REFRESH_TOKEN}`, {
          refresh_token: refreshToken,
        });
        const data = resp.data?.data ?? resp.data;
        const newAccess: string = data.access_token ?? data.accessToken;
        const newRefresh: string | undefined = data.refresh_token ?? data.refreshToken;
        localStorage.setItem('accessToken', newAccess);
        if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
        original.headers.Authorization = `Bearer ${newAccess}`;
        return axiosInstance(original);
      } catch (refreshErr) {
        clearAndRedirect();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 401 && !isAuthCall) {
      clearAndRedirect();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
